import { ACTIONS, AGENT_IDS, fmtEth, fmtTokens, type AgentId } from '@quintessence/core';
import { Link, useParams } from 'react-router-dom';
import { ChartFrame } from '../charts/ChartFrame';
import { Heatmap } from '../charts/Heatmap';
import { HBars } from '../charts/HBars';
import { TimeChart } from '../charts/TimeChart';
import { fmtDateTime } from '../charts/scale';
import { AgentAvatar } from '../components/AgentAvatar';
import { ActionValues, InstinctMeter } from '../components/BrainReadout';
import { Icon } from '../components/icons';
import { NeuralField } from '../components/NeuralField';
import { NeuralGraph } from '../components/NeuralGraph';
import { ago, sym, useSnapshot } from '../lib';
import { BRAND, SERIES } from '../theme';

/** History arrays are sampled once per scored decision; plot them against that sequence. */
const asSeries = (values: number[], total: number) => values.map((v, i) => ({ t: total - values.length + i + 1, v }));

export function AgentPage() {
  const { id } = useParams();
  const { view, config } = useSnapshot();
  const agentId = (AGENT_IDS as readonly string[]).includes(id ?? '') ? (id as AgentId) : null;

  if (!agentId) {
    return (
      <main className="container section">
        <h1>Unknown agent</h1>
        <Link to="/dashboard">Back to the dashboard</Link>
      </main>
    );
  }
  const agent = view?.agents.find((a) => a.id === agentId);
  if (!view || !agent) {
    return (
      <main className="container dash-loading">
        <div className="panel loading-card">
          <div className="spinner" aria-hidden />
          <h2>Opening the brain…</h2>
        </div>
      </main>
    );
  }

  const b = agent.brain;
  const color = SERIES[agentId];
  const p = agent.persona;
  const symbol = sym(config);
  const regimeNames = view.regimes.map((r) => r.name);
  const cells = b.cells.map((c) => ({ row: c.regime, col: ACTIONS.indexOf(c.action as (typeof ACTIONS)[number]), value: c.mean, n: c.n }));
  const champion = b.genomes.find((g) => g.id === b.championId);
  const x = agent.vault?.extra ?? {};

  return (
    <main className="container agent-page" style={{ ['--agent' as string]: color }}>
      <nav className="crumbs">
        <Link to="/dashboard">Dashboard</Link> <span aria-hidden>/</span> {p.name}
      </nav>
      <header className="agent-hero panel">
        <NeuralField key={agentId} variant="ambient" tint={BRAND[agentId]} focus={[0.76, 0.5]} density={4200} />
        <AgentAvatar id={agentId} size={72} />
        <div className="agent-hero-copy">
          <span className="kicker">
            {p.role} agent · {p.title}
          </span>
          <h1>{p.name}</h1>
          <p className="tagline">{p.tagline}</p>
          <p className="muted">{p.objective}</p>
        </div>
        <div className="agent-hero-iq">
          <span className="hero-figure">{b.iq}</span>
          <span className="muted">brain IQ</span>
        </div>
      </header>

      <section className="kv-strip panel">
        <div>
          <span className="k">Generation</span>
          <span className="v">{b.generation}</span>
        </div>
        <div>
          <span className="k">Memories</span>
          <span className="v">{b.experiences.toLocaleString()}</span>
        </div>
        <div>
          <span className="k">Training steps</span>
          <span className="v">{fmtTokens(b.trainSteps)}</span>
        </div>
        <div>
          <span className="k">Parameters</span>
          <span className="v">{b.parameters.toLocaleString()}</span>
        </div>
        <div>
          <span className="k">Directional accuracy</span>
          <span className="v">{Math.round(b.directionalAccuracy * 100)}%</span>
        </div>
        <div>
          <span className="k">Calibration error</span>
          <span className="v">{b.calibrationError.toFixed(2)}</span>
        </div>
        <div>
          <span className="k">Vault</span>
          <span className="v">{fmtEth(agent.vault?.ethBalance ?? 0)} ETH</span>
        </div>
        <div>
          <span className="k">Spent</span>
          <span className="v">{fmtEth(agent.vault?.totalEthSpent ?? 0)} ETH</span>
        </div>
        <div>
          <span className="k">{agentId === 'pyre' ? 'Burned' : agentId === 'hoard' ? 'Reserve' : agentId === 'rain' ? 'Airdropped' : 'LP ETH'}</span>
          <span className="v">
            {agentId === 'pyre'
              ? `${fmtTokens(x.totalBurned ?? 0)} ${symbol}`
              : agentId === 'hoard'
                ? `${fmtTokens(agent.vault?.tokenBalance ?? 0)} ${symbol}`
                : agentId === 'rain'
                  ? `${fmtTokens(x.totalDistributed ?? 0)} ${symbol}`
                  : fmtEth(x.ethToLiquidity ?? 0)}
          </span>
        </div>
      </section>

      <section className="panel network-panel">
        <div className="chart-head">
          <div>
            <h3 className="chart-title">Inside {p.name}’s network</h3>
            <p className="chart-sub">
              The signals it weighs most, flowing through its hidden layers (drawn at reduced scale) to the value of each action. Hover a signal to trace it.
            </p>
          </div>
        </div>
        <NeuralGraph agent={agent} />
      </section>

      <section className="dash-row one-one">
        <div className="panel decision-panel">
          <h3 className="chart-title">Latest decision</h3>
          <p className="chart-sub">
            {agent.lastThought ? `${agent.lastThought.regime} regime · ${ago(agent.lastThought.t, view.t)}` : 'No decision yet'}
          </p>
          <p className="readout-thought">“{agent.lastThought?.summary ?? 'Observing the market…'}”</p>
          <ActionValues agent={agent} />
          <InstinctMeter weight={b.instinctWeight} color={color} />
          <p className="muted small">
            Bars show each action’s predicted value; the whisker is the spread across the five networks, which is the brain’s uncertainty.
          </p>
        </div>
        <ChartFrame
          title="What the brain pays attention to"
          subtitle="Share of input sensitivity across its recent decisions (gradient saliency)."
          table={{ columns: ['Signal', 'Share', 'Latest value'], rows: b.attention.map((s) => [s.label, `${(s.weight * 100).toFixed(1)}%`, s.value]) }}
        >
          <HBars items={b.attention.slice(0, 10).map((s) => ({ label: s.label, value: s.weight }))} color={color} format={(v) => `${(v * 100).toFixed(1)}%`} />
        </ChartFrame>
      </section>

      <section className="dash-row three">
        <ChartFrame title="IQ" subtitle="Over its most recent scored decisions." table={{ columns: ['Decision', 'IQ'], rows: asSeries(b.iqHistory, b.experiences).map((p) => [`#${p.t}`, p.v]) }}>
          <TimeChart data={asSeries(b.iqHistory, b.experiences)} label="IQ" color={color} format={(v) => v.toFixed(0)} height={140} xMode="index" />
        </ChartFrame>
        <ChartFrame title="Reward (EMA)" subtitle="Smoothed realised reward." table={{ columns: ['Decision', 'Reward'], rows: asSeries(b.rewardHistory, b.experiences).map((p) => [`#${p.t}`, p.v.toFixed(3)]) }}>
          <TimeChart data={asSeries(b.rewardHistory, b.experiences)} label="Reward EMA" color={color} format={(v) => v.toFixed(2)} height={140} xMode="index" />
        </ChartFrame>
        <ChartFrame title="Training loss" subtitle="Huber loss of the ensemble." table={{ columns: ['Decision', 'Loss'], rows: asSeries(b.lossHistory, b.experiences).map((p) => [`#${p.t}`, p.v.toFixed(4)]) }}>
          <TimeChart data={asSeries(b.lossHistory, b.experiences)} label="Loss" color={color} format={(v) => v.toFixed(3)} height={140} zero xMode="index" />
        </ChartFrame>
      </section>

      <section className="dash-row one-one top">
        <ChartFrame
          title="Reward by regime and action"
          subtitle="Average realised reward of every decision, by the market regime it was made in."
          table={{ columns: ['Regime', 'Action', 'Mean reward', 'Decisions'], rows: b.cells.map((c) => [regimeNames[c.regime] ?? c.regime, c.action, c.mean.toFixed(3), c.n]) }}
        >
          <Heatmap rows={regimeNames} cols={ACTIONS.map((a) => a.toLowerCase())} cells={cells} />
        </ChartFrame>
        <div className="panel lessons">
          <h3 className="chart-title">
            <Icon name="book" size={16} /> Lessons
          </h3>
          {b.lessons.length ? (
            <ul>
              {b.lessons.map((l) => (
                <li key={l.id}>
                  <i className="legend-key legend-dot" style={{ background: color }} />
                  <p>
                    {l.text}
                    <span className="muted">
                      {' '}
                      · {Math.round(l.confidence * 100)}% confidence · {l.support} decisions
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty">No lesson is statistically clear yet.</p>
          )}
        </div>
      </section>

      <section className="dash-row one-one top">
        <div className="panel padded">
          <h3 className="chart-title">
            <Icon name="dna" size={16} /> Strategy genomes
          </h3>
          <p className="chart-sub">
            Generation {b.generation}. Champion #{champion?.id}: aggression {champion?.aggression.toFixed(2)}, patience {champion?.patience.toFixed(2)}.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Genome</th>
                  <th>Aggression</th>
                  <th>Patience</th>
                  <th>Dip bias</th>
                  <th>Curiosity</th>
                  <th>Fitness</th>
                  <th>Trials</th>
                </tr>
              </thead>
              <tbody>
                {[...b.genomes]
                  .sort((a, c) => c.fitness - a.fitness)
                  .map((g) => (
                    <tr key={g.id} className={g.id === b.championId ? 'champion' : ''}>
                      <td>
                        #{g.id}
                        {g.id === b.championId && <span className="chip">champion</span>}
                        {g.parentId != null && <span className="muted"> ← #{g.parentId}</span>}
                      </td>
                      <td>{g.aggression.toFixed(2)}</td>
                      <td>{g.patience.toFixed(2)}</td>
                      <td>{g.dipBias.toFixed(2)}</td>
                      <td>{g.curiosity.toFixed(2)}</td>
                      <td>{g.fitness.toFixed(3)}</td>
                      <td>{g.trials}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="panel thoughts">
          <h3 className="chart-title">
            <Icon name="brain" size={16} /> Thought stream
          </h3>
          <ul>
            {agent.thoughts.slice(0, 14).map((t) => (
              <li key={t.t} className={t.acted ? 'acted' : ''}>
                <span className="thought-meta">
                  <span className={`chip ${t.acted ? '' : 'chip-quiet'}`}>{t.acted ? t.size.toLowerCase() : 'wait'}</span>
                  {fmtDateTime(t.t)} · {Math.round(t.confidence * 100)}%
                </span>
                <p>{t.summary}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
